import { useState, useEffect, useRef } from "react";
import "./chartGuide.css";

function computeAbovePlacement({ rect, bubbleWidth, bubbleHeight, bottomNavbarHeight }) {
  const margin = 12;
  const gap = 24;
  const maxLeft = window.innerWidth - bubbleWidth - margin;

  const top = Math.max(rect.top - gap - bubbleHeight, margin);

  const centeredLeft = rect.left + rect.width / 2 - bubbleWidth / 2;
  const left = Math.min(Math.max(centeredLeft, margin), Math.max(maxLeft, margin));

  const ARROW_SIZE = 20;
  const targetCenter = rect.left + rect.width / 2;
  const rawPercent = ((targetCenter - left) / bubbleWidth) * 100;
  const limit = (ARROW_SIZE / bubbleWidth) * 100 + 4;
  const arrowOffset = `${Math.min(Math.max(rawPercent, limit), 100 - limit)}%`;

  return { top, left, arrow: "bottom", arrowOffset };
}

const MobileChartGuideSteps = ({ setShowGuide }) => {
  const steps = [
    {
      id: 1,
      target: "chart-guide-asset-wheel",
      text: "Your active list powers this wheel. Press, hold, and swipe to move between the assets you want to trade.",
    },
    {
      id: 2,
      target: "chart-guide-timeframe-wheel",
      text: "Choose how much time each candle represents. Press, hold, and swipe to move between timeframes and change your view of the market.",
    },
    {
      id: 3,
      target: "chart-guide-drawing-tools",
      text: "Mark up your chart with trendlines and other drawing tools. Use them to map levels, structure, and ideas.",
    },
    {
      id: 4,
      target: "chart-guide-trade-arrows",
      text: "Tap here to open the trade panel and place an order directly from the chart.",
    },
    {
      id: 5,
      target: "chart-guide-settings",
      text: "Customize your chart here. Add overlays and indicators, change the chart type, set alerts, or add this asset to a Watchlist.",
    },
    {
      id: 6,
      target: "chart-guide-share",
      text: "Share what you see. Post your chart to the Feed with drawings, indicators, and a caption to show how you’re reading the market.",
    },
  ];

  const [stepIndex, setStepIndex] = useState(0);
  const [bubblePosition, setBubblePosition] = useState({
    top: -1000,
    left: -1000,
  });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isPositioned, setIsPositioned] = useState(false);
  const current = steps[stepIndex];
  const bubbleRef = useRef(null);

  useEffect(() => {
    const prevHighlighted = document.querySelectorAll(".chart-guide-highlight");
    prevHighlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));

    const targetElement = document.getElementById(current.target);
    if (targetElement) {
      targetElement.classList.add("chart-guide-highlight");
    }

    return () => {
      const highlightedElement = document.getElementById(current.target);
      if (highlightedElement) {
        highlightedElement.classList.remove("chart-guide-highlight");
      }
    };
  }, [current.target]);

  useEffect(() => {
    const bottomNavbarHeight = 64;

    const calculatePosition = () => {
      const targetElement = document.getElementById(current.target);
      if (!targetElement) return;

      const compute = () => {
        const rect = targetElement.getBoundingClientRect();
        const bubbleElement = bubbleRef.current;
        const bubbleWidth = bubbleElement?.offsetWidth || 280;
        const bubbleHeight = bubbleElement?.offsetHeight || 200;

        const { top, left, arrow, arrowOffset } = computeAbovePlacement({
          rect,
          bubbleWidth,
          bubbleHeight,
          bottomNavbarHeight,
        });

        setBubblePosition({ top, left });
        setArrowPosition(arrow);
        setArrowOffset(arrowOffset);
        setIsPositioned(true);
      };

      compute();
      setTimeout(compute, 150);
    };

    const timer = setTimeout(calculatePosition, 60);

    window.addEventListener("resize", calculatePosition, { passive: true });
    window.addEventListener("scroll", calculatePosition, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition);
    };
  }, [current.target, stepIndex]);

  const handleNext = () => {
    if (stepIndex < steps.length - 1) {
      setIsPositioned(false);
      setBubblePosition({ top: -1000, left: -1000 });
      setStepIndex(stepIndex + 1);
      return;
    }
    setShowGuide(false);
  };

  const handleSkip = () => {
    const highlighted = document.querySelectorAll(".chart-guide-highlight");
    highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    setShowGuide(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const highlighted = document.querySelectorAll(".chart-guide-highlight");
      highlighted.forEach((el) => el.classList.remove("chart-guide-highlight"));
    };
  }, []);

  return (
    <>
      <div className="chart-guide-backdrop" onClick={handleSkip} />

      <div
        ref={bubbleRef}
        className={`chart-guide-bubble dynamic-position arrow-${arrowPosition} step-${stepIndex + 1} mobile-chart-guide-bubble`}
        style={{
          top: bubblePosition.top,
          left: bubblePosition.left,
          opacity: isPositioned ? 1 : 0,
          "--arrow-offset": arrowOffset,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div className="guide-step-indicator">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <div className="guide-text">{current.text}</div>

        <div className="guide-buttons">
          <button className="next-btn" onClick={handleNext}>
            {stepIndex === steps.length - 1 ? "Done" : "Next"}
          </button>
          <button className="skip-btn" onClick={handleSkip}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileChartGuideSteps;
